import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { Events, useEventBus } from '@/helpers/event-bus'
import { useProjectStore } from '@/stores/project'
import { useProjectCalculator } from '@/stores/projectCenter/projectCalculator'
import * as Sentry from '@sentry/vue'
import { partForRole } from '@/helpers/project-roles'

export const projectValidityGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> => {
  const projectStore = useProjectStore()
  const project = projectStore.getProject
  const meta = projectStore.getMeta
  const validRoles = projectStore.getValidRoles
  const calculator = useProjectCalculator()
  const eventBus = useEventBus()

  const projectGeneralValidity = calculator.checkValidity('general', project?.general?.data)

  if ((to.name === 'setup-project-view' || to.name === 'service-project-view') && !projectGeneralValidity) {
    // redirect to general project view if project data is not valid
    const generalError = meta?.canEdit ? 'errors.project.general-invalid' : 'errors.project.general-invalid-noedit'
    const redirectTo = meta?.canEdit ? 'general-project-view' : 'summary-project-view'
    eventBus.emit(Events.SHOW_ERROR, { key: generalError })
    return next({ name: redirectTo })
  }

  // Check if at least ONE assigned role has valid setup data
  // Only check roles that BOTH:
  // 1. Are assigned to the user (in validRoles)
  // 2. Actually exist in the project (have their setup part initialized)
  const assignedRoleValidities: boolean[] = []
  const rolesWithoutParts: string[] = []

  for (const role of validRoles ?? []) {
    const setupPart = partForRole(role, 'setup')
    if (!setupPart) continue

    const part = project?.[setupPart]
    if (part) {
      assignedRoleValidities.push(calculator.checkValidity(setupPart, part.data))
    } else {
      rolesWithoutParts.push(role)
    }
  }

  // At least one role must be valid to proceed to service view
  const atLeastOneRoleValid = assignedRoleValidities.length > 0 && assignedRoleValidities.some((valid) => valid)

  if (to.name === 'service-project-view' && !atLeastOneRoleValid) {
    // redirect to setup-project-view if NO role has valid data
    const setupError = 'errors.project.setup-invalid.NONE'
    Sentry.logger.warn('No valid role with data', {
      assignedValidities: JSON.stringify(assignedRoleValidities),
      rolesWithoutParts: JSON.stringify(rolesWithoutParts),
    })
    eventBus.emit(Events.SHOW_ERROR, { key: setupError })
    return next({ name: 'setup-project-view', params: { currentTabIndex: 0 } })
  }
  // valid routing - go on
  return next()
}
